import { useState } from "react";
import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { zLayer } from "../../zIndex";
import { FloatingLayerProvider } from "../../floatingLayer";
import { Button } from "../Button";
import { Icon } from "../Icon";
import type { IconType } from "../Icon";
import { TabbedTabContext, useTabbedTabContext } from "./useTabbedTabContext";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";

export type TabbedNavigationTabType = {
  label: string;
  index: number;
};

export const TabbedDrawerRoot = ({
  active,
  setActive,
  children,
  initTab,
  hideCloseIcon = false,
  backdropClassName,
  containerClassName,
  panelClassName,
  closeButtonClassName,
  closeIcon,
  appear = false,
}: {
  active: boolean;
  setActive: (value: boolean) => void;
  children: ReactNode;
  initTab?: number;
  hideCloseIcon?: boolean;
  backdropClassName?: string;
  containerClassName?: string;
  panelClassName?: string;
  closeButtonClassName?: string;
  closeIcon?: IconType;
  appear?: boolean;
}) => {
  const resolvedInitTab = initTab ?? 0;
  const [tab, setTab] = useState(resolvedInitTab);
  const [prevActive, setPrevActive] = useState(active);
  const [prevInitTab, setPrevInitTab] = useState(resolvedInitTab);

  // Reset to init tab when the drawer opens or initTab changes while open.
  if (active !== prevActive || resolvedInitTab !== prevInitTab) {
    setPrevActive(active);
    setPrevInitTab(resolvedInitTab);
    if (active) {
      setTab(resolvedInitTab);
    }
  }

  return (
    <TabbedTabContext.Provider
      value={{
        tab,
        setTab,
      }}
    >
      <Transition show={active} appear={appear}>
        <Dialog
          onClose={() => setActive(false)}
          className={`relative ${zLayer.drawer}`}
        >
          <TransitionChild>
            <DialogBackdrop
              className={twMerge(
                "fixed inset-0 bg-black/30 transition duration-300 ease-in-out data-closed:opacity-0",
                backdropClassName,
              )}
            />
          </TransitionChild>

          <TransitionChild>
            <div
              className={twMerge(
                "fixed inset-0 flex w-screen items-center justify-end transition duration-300 ease-in-out data-closed:translate-x-full md:p-4",
                containerClassName,
              )}
            >
              <DialogPanel
                className={twMerge(
                  "flex h-full w-[490px] max-w-[100vw] flex-col overflow-hidden bg-white md:rounded-lg dark:border-l dark:border-zinc-800 dark:bg-zinc-900",
                  panelClassName,
                )}
              >
                {!hideCloseIcon && (
                  <Button
                    color="transparent"
                    size="small"
                    className={twMerge(
                      "absolute top-5 right-5 dark:text-zinc-400 dark:hover:text-zinc-100",
                      closeButtonClassName,
                    )}
                    onClick={() => setActive(false)}
                  >
                    <Icon icon={closeIcon ?? "CLOSE"} />
                  </Button>
                )}

                <FloatingLayerProvider host="drawer">
                  {children}
                </FloatingLayerProvider>
              </DialogPanel>
            </div>
          </TransitionChild>
        </Dialog>
      </Transition>
    </TabbedTabContext.Provider>
  );
};

export const TabbedDrawerCTA = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={twMerge(
        "relative bottom-0 mt-auto flex w-full flex-col gap-2 p-4 dark:border-t dark:border-zinc-800 dark:bg-zinc-900/50",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const TabbedDrawerHeader = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={twMerge(
        "flex flex-col rounded-t-lg bg-zinc-50 dark:bg-zinc-900/50",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const TabbedDrawerNavigation = ({
  tabs,
  className,
  tabClassName,
  activeTabClassName,
}: {
  tabs: TabbedNavigationTabType[];
  className?: string;
  tabClassName?: string;
  activeTabClassName?: string;
}) => {
  const { tab: currentTab, setTab } = useTabbedTabContext();

  return (
    <div
      className={twMerge(
        "flex w-full items-center border-b border-zinc-300 dark:border-zinc-800",
        className,
      )}
    >
      {tabs.map((tab) => (
        <div
          key={tab.index}
          onClick={() => setTab(tab.index)}
          className={twMerge(
            "grow cursor-pointer border-b-4 border-transparent px-2 py-4 text-center font-semibold text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800",
            tabClassName,
            currentTab === tab.index &&
              "border-zinc-800 text-zinc-800 dark:border-zinc-100 dark:text-zinc-100",
            currentTab === tab.index && activeTabClassName,
          )}
        >
          <span>{tab.label}</span>
        </div>
      ))}
    </div>
  );
};

export const TabbedDrawerTab = ({
  tab,
  children,
  className,
}: {
  tab: number;
  children: ReactNode;
  className?: string;
}) => {
  const { tab: currentTab } = useTabbedTabContext();

  if (tab !== currentTab) return null;

  return (
    <div className={twMerge("h-full overflow-y-auto", className)}>
      {children}
    </div>
  );
};

export const TabbedDrawerTitle = ({
  title,
  subtitle,
  icon = "FILE_DOCUMENT_CHECK_OUTLINE",
  imageUrl,
  customIcon,
  onClick,
  className,
  iconContainerClassName,
  titleClassName,
  subtitleClassName,
}: {
  title: string;
  subtitle?: ReactNode;
  icon?: IconType;
  imageUrl?: string;
  customIcon?: ReactNode;
  onClick?: () => void;
  className?: string;
  iconContainerClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
}) => {
  return (
    <div
      className={twMerge(
        "flex items-center gap-4 px-8 pt-10 pb-4",
        !!onClick &&
          "cursor-pointer rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800",
        className,
      )}
      onClick={onClick}
    >
      {customIcon ? (
        customIcon
      ) : (
        <span
          className={twMerge(
            "rounded-lg bg-zinc-200 p-3 dark:bg-zinc-800 dark:text-zinc-400",
            iconContainerClassName,
          )}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="icon"
              className="h-7 w-7 rounded-full object-cover"
            />
          ) : (
            <Icon icon={icon} size={1.1} />
          )}
        </span>
      )}
      <div className="flex flex-col gap-1">
        <span
          className={twMerge(
            "font-semibold dark:text-zinc-100",
            titleClassName,
          )}
        >
          {title}
        </span>
        {!!subtitle && (
          <span
            className={twMerge("text-sm dark:text-zinc-400", subtitleClassName)}
          >
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
};

export const TabbedDrawer = TabbedDrawerRoot;
